import { Link } from "react-router-dom";
import { MapPin, Heart, MessageCircle, Clock3, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { useState, useEffect, memo } from "react";
import { CATEGORIES, formatPrice, waLink, STATUS_META, imgSrc } from "@/lib/api";

const StatusIcon = ({ status }) =>
  status === "approved" ? <CheckCircle2 size={13} /> : status === "pending" ? <Clock3 size={13} /> : status === "revision" ? <AlertTriangle size={13} /> : <XCircle size={13} />;

function ListingCard({ listing, showStatus = false }) {
  const [faved, setFaved] = useState(false);
  useEffect(() => {
    try {
      const favs = JSON.parse(localStorage.getItem("alsat67_favs") || "[]");
      setFaved(favs.includes(listing.id));
    } catch (e) {
      setFaved(false);
    }
  }, [listing.id]);

  const toggleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let favs = [];
    try { favs = JSON.parse(localStorage.getItem("alsat67_favs") || "[]"); } catch (err) { favs = []; }
    const next = favs.includes(listing.id) ? favs.filter((f) => f !== listing.id) : [...favs, listing.id];
    localStorage.setItem("alsat67_favs", JSON.stringify(next));
    setFaved(next.includes(listing.id));
  };

  const catName = CATEGORIES.find((c) => c.id === listing.category)?.name || listing.category;
  const status = STATUS_META[listing.status];

  return (
    <Link
      to={`/ilan/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-black/5 bg-card shadow-sm hover:shadow-xl hover:shadow-coral/10 hover:-translate-y-1.5 transition-all duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {listing.images?.[0] ? (
          <img
            src={imgSrc(listing.images[0], 600)}
            alt={listing.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full place-items-center text-slate-300 text-4xl font-black font-display">67</div>
        )}
        <span className="clip-tag absolute left-0 top-0 bg-emerald2/90 px-3 py-1.5 text-[11px] font-bold text-bone backdrop-blur">
          {listing.district}
        </span>
        <span className="absolute right-2 top-2 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-bold text-slate-700 backdrop-blur">
          {catName}
        </span>
        <button
          data-testid={`favorite-button-${listing.id}`}
          onClick={toggleFav}
          aria-label="Favorilere ekle"
          className={`absolute bottom-2 right-2 grid h-9 w-9 place-items-center rounded-full backdrop-blur transition ${faved ? "bg-coral text-white" : "bg-white/90 text-slate-600 hover:text-coral"}`}
        >
          <Heart size={16} fill={faved ? "currentColor" : "none"} />
        </button>
        <span className="absolute bottom-2 left-2 rounded-full bg-emerald-600 px-3 py-1 font-mono text-sm font-bold text-white shadow-lg">
          {formatPrice(listing.price)}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3.5">
        <h3 className="font-display font-semibold leading-snug line-clamp-2 text-[15px]">{listing.title}</h3>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
            <MapPin size={12} className="text-coral" /> {listing.district}, Zonguldak
          </span>
          {showStatus && listing.status ? (
            <span data-testid={`listing-status-${listing.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${status.cls}`}>
              <StatusIcon status={listing.status} /> {status.label}
            </span>
          ) : (
            <a
              data-testid={`card-whatsapp-${listing.id}`}
              href={waLink(listing.phone, listing.title)}
              target="_blank" rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="grid h-8 w-8 place-items-center rounded-full bg-wa/10 text-wa hover:bg-wa hover:text-white transition-colors"
              aria-label="WhatsApp ile iletişim"
            >
              <MessageCircle size={15} />
            </a>
          )}
        </div>
      </div>
    </Link>
  );
}

export default memo(ListingCard);
